import {
  createFileRoute,
  stripSearchParams,
  type ErrorComponentProps,
} from '@tanstack/react-router'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import { marketNewsService } from '@/features/news/api/marketNewsService'
import { useNewsStories } from '@/features/news/api/queries'
import NewsArticleList from '@/features/news/components/NewsArticleList'
import { STORIES_SKELETON } from '@/features/news/components/news-skeletons'
import NewsFilters from '@/features/news/components/NewsFilters'
import NewsPageTabs from '@/features/news/components/NewsPageTabs'
import { NEWS_FILTER_DEFAULTS } from '@/features/news/filters'
import { loadMarketNews, storiesQueryFor } from '@/screens/market/MarketNewsScreen.loader'
import {
  MARKET_NEWS_SEARCH_DEFAULTS,
  filtersFromSearch,
  marketNewsSearchSchema,
  searchFromFilters,
} from '@/screens/market/news-search'

const HEADER = <TitleRow title="Market News" />

function MarketNewsPending() {
  return (
    <ScreenLayout pending pendingLabel="Loading the news…" header={HEADER} pageTitle="Market News">
      <div className="flex flex-col gap-4">
        <NewsPageTabs value="news" />
        <Skeleton className="h-11 w-full lg:h-9" />
      </div>
      {STORIES_SKELETON}
    </ScreenLayout>
  )
}

function MarketNewsError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      header={HEADER}
      title="Could not load the news"
      description="We could not get the latest stories. Check your connection and try again."
    />
  )
}

export const Route = createFileRoute('/_app/market/news')({
  validateSearch: marketNewsSearchSchema,
  search: { middlewares: [stripSearchParams(MARKET_NEWS_SEARCH_DEFAULTS)] },
  loader: loadMarketNews,
  pendingComponent: MarketNewsPending,
  errorComponent: MarketNewsError,
  component: MarketNewsScreen,
})

/** The quiet line by the tabs while the first page loads (mock `.nw-title__status`). */
function useLoadingStatus(query: ReturnType<typeof storiesQueryFor>): string | undefined {
  const stories = useNewsStories(query)
  return marketNewsService.available && stories.isPending && stories.fetchStatus === 'fetching'
    ? 'Getting the latest stories'
    : undefined
}

/**
 * Market update's News tab (mock `#/market/news`): the news sites' stories, under the same
 * search · Sort · Filters row as Updates, with a Language switch in the dialog.
 *
 * mm-be has no news endpoint, so the whole list sits behind `marketNewsService.available`: the
 * page draws its real head and the generic "not live yet" state, and asks nothing (owner ruling
 * 1, 2026-10-28; contract in `asks/nw1.md`).
 */
function MarketNewsScreen() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const filters = filtersFromSearch(search)
  const query = storiesQueryFor(search)
  const status = useLoadingStatus(query)

  return (
    <ScreenLayout header={HEADER} pageTitle="Market News">
      <div className="flex flex-col gap-4">
        <NewsPageTabs value="news" status={status} />
        <NewsFilters
          placeholder="Search news, company or source"
          query={search.query ?? ''}
          onQueryChange={(next) => {
            const trimmed = next.trim()
            void navigate({
              search: (prev) => ({ ...prev, query: trimmed === '' ? undefined : trimmed }),
            })
          }}
          sort={search.sort}
          onSortChange={(sort) => {
            void navigate({ search: (prev) => ({ ...prev, sort }) })
          }}
          filters={filters}
          onFiltersChange={(next) => {
            void navigate({ search: (prev) => ({ ...prev, ...searchFromFilters(next) }) })
          }}
          lang={search.lang}
          onLangChange={(lang) => {
            void navigate({ search: (prev) => ({ ...prev, lang }) })
          }}
        />
      </div>

      <NewsArticleList
        query={query}
        onClear={() => {
          void navigate({
            search: { ...MARKET_NEWS_SEARCH_DEFAULTS, ...searchFromFilters(NEWS_FILTER_DEFAULTS) },
          })
        }}
      />
    </ScreenLayout>
  )
}
